import React, { useEffect, useState, useRef } from 'react'
import { CSSTransition } from 'react-transition-group'
import {
    TopBar,
    Spinner,
    TimedAlert,
    RemainDepositBadge,
    EmptyList
} from 'components'
import {
    useAppSelector,
    useBindDispatch,
    useInfiniteScroll,
    useAnalytics
} from 'hooks'
import { numberFormatter, constants, getConnectUser } from 'helpers'
import { useTranslation } from 'react-i18next'
import { DepositDetailsType } from 'storeTypes'
import { SingleDeposit } from './SingleDeposit'
import { VeIcon } from 'assets'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { colors } from 'styles/colors'

export const Deposits = () => {
    const { t } = useTranslation()
    const navigator = useNavigate()
    const [animate, setAnimate] = useState(false)
    const [animationList, setAnimationList] = useState(false)
    const nodeRef = useRef(null)
    const { username } = getConnectUser()
    const { veDepositsHandler } = useBindDispatch()
    const { trackEvent } = useAnalytics()

    const {
        loading,
        veDepositData,
        error: depositsError
    } = useAppSelector((store) => store.veDeposit[username] || {})
    const { userData } = useAppSelector((store) => store.home)
    useEffect(() => {
        setAnimate(true)
        trackEvent({
            category: constants.ANALYTICS.VE_PROFILE.VE_PROFILE,
            action: constants.ANALYTICS.VE_PROFILE.VE_DEPOSIT_ACTION,
            name: constants.ANALYTICS.VE_PROFILE.VE_DEPOSIT_OPENED
        })
        if (!veDepositData) {
            veDepositsHandler({ veCode: username })
        }
    }, [])
    const [veNameParam] = useSearchParams()
    const veName = veNameParam.get('ve_name')

    const { name } = userData || { name: veName }

    const { renderedItems } = useInfiniteScroll({
        filteredItems: veDepositData?.deposits || [],
        itemsPerLoad: constants.ITEMS_PER_LOAD,
        createItemCard: (item, index) => (
            <SingleDeposit
                key={index}
                deposit_date={item.deposit_date}
                amount={item.amount}
                reference_code={item.reference_code}
                source={item.source}
                t={t}
            />
        ),
        containerClassName: 'deposits--container'
    })

    const depositsRender = ({
        remainingDepositTotal
    }: {
        depositList: DepositDetailsType[]
        remainingDepositTotal: number
    }) => (
        <CSSTransition
            unmountOnExit
            timeout={constants.ANIMATION_TIMEOUT}
            in={animate}
            classNames="generic"
            appear
            onEnter={() => setAnimationList(true)}
            nodeRef={nodeRef}
        >
            <div className="deposits--parent" ref={nodeRef}>
                <div className="deposits--container">
                    <div
                        aria-label="deposits-card"
                        className={
                            animationList
                                ? 'animate-show-2 animate-showed-2'
                                : 'animate-show-2'
                        }
                    >
                        <h2 className="deposits--header">
                            {t('deposits.pageTitle')}
                        </h2>
                        {remainingDepositTotal > 0 && (
                            <div className="deposits--remain-container">
                                <RemainDepositBadge
                                    totalRemainingDeposit={numberFormatter(
                                        remainingDepositTotal
                                    )}
                                />
                            </div>
                        )}
                        {renderedItems}
                    </div>
                </div>
            </div>
        </CSSTransition>
    )

    const renderDepositPage = () => {
        if (loading) return <Spinner size={'90'} pageSpinner={true} />
        if (depositsError) {
            return (
                <TimedAlert type="floating" status="error">
                    {depositsError}
                </TimedAlert>
            )
        }
        if (veDepositData?.deposits?.length) {
            return depositsRender({
                depositList: veDepositData.deposits,
                remainingDepositTotal: veDepositData.gap || 0
            })
        }
        return (
            <div className="deposits--empty-container">
                <EmptyList message={t('noDeposit')} />
            </div>
        )
    }

    const depositPage = renderDepositPage()

    return (
        <>
            <div className="sticky-header" id="sticky-header">
                <TopBar
                    back
                    backNavigation={() => navigator('/profile')}
                    color={colors.blue}
                >
                    <div className="topbar--title">
                        <VeIcon width="20" height="21" color={colors.blue} />
                        {name}
                    </div>
                </TopBar>
            </div>
            {depositPage}
        </>
    )
}
